import React, { useState } from 'react';
// ⚠️ `Zap`, not `PlugConnected` — that one is not a lucide icon, and an icon that does not
// exist in lucide-react imports as `undefined` and only explodes when React tries to render
// it, i.e. when Rob opens the screen, not at build time.
import { Key, Eye, EyeOff, Save, Zap, Loader2, Check, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { superadminApi } from '../lib/api';
import { toast } from 'sonner';

/**
 * One sign-in provider's credentials, on Superadmin → API Keys.
 *
 * Rob, 17/08/2026: *"Add API key details to superadmin section ... so that I do not have to
 * go in to the ENV file directly."* Same shape as the address-lookup card beside it.
 *
 * ⚠️ THERE ARE THREE STATES, NOT TWO, and the middle one is the whole point. A key saved
 * here wins, but `.env` is still a fallback — so "configured" is not the same as "configured
 * HERE". Without saying which, clearing a key in the UI appears to do nothing (it keeps
 * working from `.env`), and after a rotation that stale value is a revoked key: a good-looking
 * key on screen and failures in the log, with nothing joining the two.
 */
const SOURCE_META = {
  database:    { label: 'Configured',        cls: 'bg-green-500/20 text-green-600' },
  environment: { label: 'Using the .env file', cls: 'bg-yellow-500/20 text-yellow-600' },
  none:        { label: 'Not set',           cls: 'bg-yellow-500/20 text-yellow-600' },
};

const VERDICT_STYLE = {
  pass:    'bg-green-500/10 border border-green-500/30 text-green-700 dark:text-green-400',
  fail:    'bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-400',
  //  'unknown' is amber on purpose: the test could not tell, which is neither good news
  //  nor bad news, and dressing it as either would be a lie.
  unknown: 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-700 dark:text-yellow-300',
};

/*
 * ⚠️ `title` EXISTS BECAUSE NOT EVERY SET OF KEYS IS A SIGN-IN. This card hard-coded
 * "Sign in with {label}", which was true of the only two providers it had. The calendar
 * credentials added on 22/08/2026 are not a way to sign in — Rob's screenshot showed
 * **"Sign in with Google Calendar"**, which is a confidently wrong sentence about the one
 * card most likely to be confused with the Google sign-in card directly above it.
 * Defaults to the old wording so the two sign-in cards are unchanged.
 */
/*
 * ⚠️ `extraLabel` / `extraHint` ADD A THIRD BOX, AND ONLY THE TEAMS BOT NEEDS ONE.
 * Every other set of credentials here is an (id, secret) pair. The bot also needs the
 * tenant its app registration lives in — a third value, with no secret alongside it, that
 * the backend's `PROVIDER_FIELDS` four-tuple cannot express. Optional, so the three
 * existing cards render exactly as before.
 */
/*
 * ⚠️ `extraSecretLabel` ADDS A SECOND SECRET BOX, AND ONLY SLACK NEEDS ONE (13/10/2026).
 * Slack's signing secret — what proves a button press really came from Slack. Unlike the
 * Teams tenant id it IS a secret, so it is masked, never sent back by the server (only
 * whether one is saved), and only sent when somebody types into it — an untouched box must
 * never clear a saved secret.
 */
const SsoProviderCard = ({ provider, label, title, blurb, secretHint, config, onSaved,
                           extraLabel, extraHint, extraSecretLabel, extraSecretHint }) => {
  const state = config || { client_id: '', secret_saved: false, source: 'none' };
  const [clientId, setClientId] = useState(state.client_id || '');
  const [extra, setExtra] = useState(state.extra_value || '');
  const [secret, setSecret] = useState('');
  const [extraSecret, setExtraSecret] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const meta = SOURCE_META[state.source] || SOURCE_META.none;

  const handleReveal = async () => {
    if (revealed) { setRevealed(false); setSecret(''); return; }
    try {
      const r = await superadminApi.revealSsoSecret(provider);
      setSecret(r.data?.key || '');
      setRevealed(true);
      if (!r.data?.key) {
        // ⚠️ Say WHY it's empty. A blank box here does not mean "no secret" — it may be
        // living in .env, which is a file on the server and not ours to hand back.
        toast.info(state.source === 'environment'
          ? "Nothing saved here — the secret in use is in the server's .env file."
          : 'No secret saved yet.');
      }
    } catch {
      toast.error('Could not reveal the secret');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setResult(null);
    try {
      // ⚠️ Only send what changed. `undefined` is left alone by the API, `''` clears it —
      // so an untouched secret box must not be sent, or opening the screen and pressing
      // Save would wipe the stored secret.
      const payload = { [`${provider}_client_id`]: clientId };
      if (revealed || secret) payload[`${provider}_client_secret`] = secret;
      // ⚠️ Sent only when the card HAS a third box. Sending `undefined` for the other
      // three providers is harmless, but sending `''` would clear a value they do not own.
      if (extraLabel) payload[`${provider}_tenant_id`] = extra;
      if (extraSecretLabel && extraSecret) payload[`${provider}_signing_secret`] = extraSecret;
      const r = await superadminApi.saveSsoConfig(payload);
      toast.success('Saved — it applies immediately, no restart needed');
      setSecret('');
      setExtraSecret('');
      setRevealed(false);
      onSaved?.(r.data?.config);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setResult(null);
    try {
      const r = await superadminApi.testSsoCredentials(provider);
      setResult(r.data);
    } catch (e) {
      setResult({ verdict: 'fail', message: e.response?.data?.detail || 'The test could not run' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="card-hive p-6" data-testid={`sso-card-${provider}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-blue-500/15 text-blue-600 flex items-center justify-center">
          <Key className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-heading font-bold text-lg">{title || `Sign in with ${label}`}</h3>
          <p className="text-sm text-muted-foreground">{blurb}</p>
        </div>
      </div>

      <div className="mb-4">
        <label className="text-xs text-muted-foreground uppercase flex items-center gap-2">
          Client ID
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${meta.cls}`}>
            {meta.label}
          </span>
        </label>
        {/* Not a secret — it travels in the URL on every sign-in, so masking it would be
            theatre and would only make it harder to check against the console. */}
        <Input
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          placeholder={`Paste the ${label} client ID`}
          className="mt-1 font-mono"
          data-testid={`sso-${provider}-client-id`}
        />
      </div>

      {extraLabel && (
        <div className="mb-4">
          <label className="text-xs text-muted-foreground uppercase">{extraLabel}</label>
          {/* Not a secret — it appears in every token request URL. Masking it would be
              theatre and would make it harder to check against the Azure portal. */}
          <Input
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            placeholder={extraHint}
            className="mt-1 font-mono"
            data-testid={`sso-${provider}-extra`}
          />
        </div>
      )}

      <div className="mb-2">
        <label className="text-xs text-muted-foreground uppercase">Client secret</label>
        <div className="flex gap-2 mt-1">
          <Input
            type={revealed ? 'text' : 'password'}
            value={secret}
            onChange={(e) => { setSecret(e.target.value); setRevealed(true); }}
            placeholder={state.secret_saved
              ? '•••••••• (leave blank to keep current)'
              : secretHint}
            className="flex-1 font-mono"
            data-testid={`sso-${provider}-secret`}
          />
          <Button
            variant="secondary"
            type="button"
            onClick={handleReveal}
            title={revealed ? 'Hide secret' : 'Reveal stored secret'}
          >
            {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {extraSecretLabel && (
        <div className="mb-2 mt-4">
          <label className="text-xs text-muted-foreground uppercase flex items-center gap-2">
            {extraSecretLabel}
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${state.extra_secret_saved
              ? 'bg-green-500/20 text-green-600' : 'bg-yellow-500/20 text-yellow-600'}`}>
              {state.extra_secret_saved ? 'Saved' : 'Not set'}
            </span>
          </label>
          <Input
            type="password"
            value={extraSecret}
            onChange={(e) => setExtraSecret(e.target.value)}
            placeholder={state.extra_secret_saved
              ? '•••••••• (leave blank to keep current)'
              : extraSecretHint}
            className="mt-1 font-mono"
            data-testid={`sso-${provider}-extra-secret`}
          />
        </div>
      )}

      {state.source === 'environment' && (
        <div className="rounded-lg bg-yellow-500/10 border border-yellow-500/30 p-3 text-sm text-yellow-700 dark:text-yellow-300 mb-4">
          <AlertTriangle className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Nothing is saved here yet — sign-in is running on the values in the server's{' '}
          <code>.env</code> file. Save a key here and it takes over.
        </div>
      )}

      <p className="text-xs text-muted-foreground mb-4">
        Applies immediately, no restart needed.
        {provider === 'microsoft' && ' Microsoft secrets expire — note the date when you create one.'}
      </p>

      <div className="flex justify-between items-center">
        <Button variant="outline" type="button" onClick={handleTest} disabled={testing}
                data-testid={`sso-${provider}-test`}>
          {testing
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Testing…</>
            : <><Zap className="w-4 h-4 mr-2" />Test</>}
        </Button>
        <Button onClick={handleSave} disabled={saving} data-testid={`sso-${provider}-save`}>
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      {/* ⚠️ THREE VERDICTS, NOT TWO — for the same reason the pill has three states. The
          provider is allowed to check the authorisation code before it checks our secret, in
          which case the same answer comes back whether the secret is right or wrong. Showing
          that as green would be a tick that means nothing, which is worse than no test at
          all; showing it as red would send Rob hunting a key that is perfectly fine. */}
      {result && (
        <div className={`mt-4 rounded-lg p-3 text-sm ${VERDICT_STYLE[result.verdict] || VERDICT_STYLE.fail}`}
          data-testid={`sso-${provider}-test-result`}>
          {result.verdict === 'pass'
            ? <Check className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            : <AlertTriangle className="w-4 h-4 inline mr-1.5 -mt-0.5" />}
          {result.message}
        </div>
      )}
    </div>
  );
};

export default SsoProviderCard;
